import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { getAgendaByOrganizer, getBookingSlot, getLineupsByOrganizer, getAllTicketGroupBySlug } from "../../api";
import { useAuth } from "../../context/AuthContext";
import { useNightMode } from "../../contexts/NightModeContext";
import { useNavTitle } from "../../contexts/NavTitleContext";
import { images } from "../../assets/images";
import { 
  LayoutDashboard,
  PenSquare,
  Building,
  Layers,
  Ticket,
  CheckSquare,
  Heart,
  Percent,
  Mic,
  Coins,
  BarChart3,
  Receipt,
  UsersRound,
  MessageSquare,
  CalendarRange,
  LogOut,
  Palette,
  RotateCcw
} from 'lucide-react';
import '../../components/layout/Organizer/SideNavMenu.css';

type SideNavMenuProps = {
  minifyNav: boolean;
  mobileNavOpen: boolean;
  setMobileNavBarOpen: (value: boolean) => void;
};

const EventSideNavMenu = (props: SideNavMenuProps) => {
  const { minifyNav, mobileNavOpen, setMobileNavBarOpen } = props;
  const location = useLocation();
  const { eventId } = useParams();
  const { slugId } = useParams();
  const [hasSlotData, setHasSlotData] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [hasMultipleVenues, setHasMultipleVenues] = useState(false);
  const [hasMultipleTicketGroups, setHasMultipleTicketGroups] = useState(false);
  const { user } = useAuth();
  const { entitlements } = user || {};
  const isPayoutEnabled = entitlements && entitlements.length > 0 && entitlements?.includes("payout");
  const [hasLineupData, setHasLineupData] = useState(false);
  const [hasAgendaData, setHasAgendaData] = useState(false);
  const [isEventClosed, setIsEventClosed] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const { isNightMode, toggleNightMode } = useNightMode();

  let userRole = "";
  try {
    const authUser = localStorage.getItem("authUser");
    if (authUser) {
      const parsedData = JSON.parse(authUser);
      userRole = String(parsedData.user?.userRole ?? "");
    }
  } catch (err) {
    console.error("Error parsing authUser", err);
  }

  const { setNavTitle } = useNavTitle();

  useEffect(() => {
    let links = document.querySelectorAll(".menu--link");
    let link = location.pathname;
    links.forEach((el) => {
      if (el.getAttribute("href") === link) {
        el.classList.add("active");
        const t = (el as HTMLElement).getAttribute('title');
        if (t) setNavTitle(t);
      } else {
        el.classList.remove("active");
      }
    });
  }, [location]);

  useEffect(() => {
    const checkSlotData = async () => {
      const eventData = localStorage.getItem("eventData");
      setIsLoading(true);
      try {
        try {
          const response = await getLineupsByOrganizer();
          setHasLineupData(response && response.length > 0);
        } catch (lineupErr) {
          console.error("Failed to fetch lineups by organizer", lineupErr);
          setHasLineupData(false);
        }

        try {
          const agendaResponse = await getAgendaByOrganizer();
          setHasAgendaData(agendaResponse && agendaResponse.length > 0);
        } catch (agendaErr) {
          console.error("Failed to fetch agenda by organizer", agendaErr);
          setHasAgendaData(false);
        }

        if (eventData) {
          const eventSlug = JSON.parse(eventData).slugId;
          const data = await getBookingSlot(eventSlug);
          
          let ticketGroups = [];
          try {
            ticketGroups = await getAllTicketGroupBySlug(eventSlug);
          } catch (ticketError) {
            console.error("Failed to fetch ticket groups", ticketError);
          }

          const hasSlotRequiredGroup = Array.isArray(ticketGroups) && ticketGroups.some(
            (group: any) => group.isSlotRequired === true || group.isSlotRequired === "true"
          );

          setHasSlotData((data && data.length > 0) || hasSlotRequiredGroup);
        }
      } catch (err) {
        console.error("Failed to fetch slot data", err);
        setHasSlotData(false);
      } finally {
        setIsLoading(false);
      }
    };
    checkSlotData();
  }, [slugId]);

  const getEventName = () => {
    const eventData = localStorage.getItem("eventData");
    if (eventData) {
      const eventSlug = JSON.parse(eventData).eventName;
      return eventSlug;
    }
    return "Event Name";
  }
  const eventName = getEventName();

  useEffect(() => {
    const eventData = localStorage.getItem("eventData");
    if (eventData) {
      try {
        const response = JSON.parse(eventData);
        setHasMultipleVenues(response.hasMultipleVenues);
        setHasMultipleTicketGroups(response.hasMultipleTicketGroups);
        setIsEventClosed(response.eventStatus==="closed");
      } catch (err) {
        console.error("Invalid eventData in localStorage", err);
      }
    }
  }, []);

  useEffect(() => {
    if (!mobileNavOpen) return;
    // Close only when the page itself scrolls — not when scrolling the sidebar list.
    const handleClose = (event: Event) => {
      const target = event.target as Node | null;
      const nav = document.querySelector(".vertical_nav.vertical_nav__opened");
      if (nav && target && nav.contains(target)) return;
      setMobileNavBarOpen(false);
    };
    window.addEventListener("scroll", handleClose, { passive: true, capture: true });
    return () => {
      window.removeEventListener("scroll", handleClose, true);
    };
  }, [mobileNavOpen, setMobileNavBarOpen]);

  const [isDesktop, setIsDesktop] = useState(window.innerWidth > 1024);
  useEffect(() => {
    const handleResize = () => setIsDesktop(window.innerWidth > 1024);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const effectiveMinify = isDesktop ? true : minifyNav;
  const isExpanded = !effectiveMinify || isHovered;
  const profileName = user?.name || 'Organizer';

  return (
    <nav
      className={`vertical_nav ${effectiveMinify ? "vertical_nav__minify" : ""} ${
        mobileNavOpen ? "vertical_nav__opened" : ""
      } ${isHovered ? "sidebar-hovered" : ""}`}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Top Brand Logo Section */}
      <Link to="/home" className="sidebar-logo-section">
        {/* Full Expanded Logo */}
        <img 
          src={isNightMode ? images.eventox_dark_logo : images.eventox_light_logo} 
          alt="Eventox Logo" 
          className="logo-expanded" 
          style={{
            opacity: isExpanded ? 1 : 0,
            transform: isExpanded ? 'translate(-50%, -50%) scale(1)' : 'translate(-50%, -50%) scale(0.5)',
            pointerEvents: isExpanded ? 'auto' : 'none'
          }}
        />

        {/* Collapsed Icon/Logo */}
        <img 
          src={isNightMode ? images.white_icon : images.grey_logo} 
          alt="Eventox Collapsed Logo" 
          className="logo-collapsed" 
          style={{
            opacity: isExpanded ? 0 : 1,
            transform: isExpanded ? 'translate(-50%, -50%) scale(0.5)' : 'translate(-50%, -50%) scale(1)',
            pointerEvents: isExpanded ? 'none' : 'auto'
          }}
        />
      </Link>

      {/* Menu Items */}
      <div className="sidebar-nav-list">
        <Link
          to={`/event/${eventId}`}
          className="sidebar-btn menu--link"
          title="Dashboard"
          onClick={() => {
            setMobileNavBarOpen(false);
          }}
        >
          <div className="active-indicator" />
          <LayoutDashboard className="sidebar-icon" />
          <span className="sidebar-label">{eventName}</span>
        </Link>

        <Link
          to={`/event/${eventId}/edit-event`}
          className="sidebar-btn menu--link"
          title="Edit Event"
          onClick={() => {
            setMobileNavBarOpen(false);
          }}
        >
          <div className="active-indicator" />
          <PenSquare className="sidebar-icon" />
          <span className="sidebar-label">Edit Event</span>
        </Link>

        {hasMultipleVenues && (
          <Link
            to={`/event/${eventId}/venue`}
            className="sidebar-btn menu--link"
            title="Venue"
            onClick={() => {
              setMobileNavBarOpen(false);
            }}
          >
            <div className="active-indicator" />
            <Building className="sidebar-icon" />
            <span className="sidebar-label">Venue</span>
          </Link>
        )}

        {hasMultipleTicketGroups && (
          <Link
            to={`/event/${eventId}/ticket-group`}
            className="sidebar-btn menu--link"
            title="Ticket Group"
            onClick={() => {
              setMobileNavBarOpen(false);
            }}
          >
            <div className="active-indicator" />
            <Layers className="sidebar-icon" />
            <span className="sidebar-label">Ticket Group</span>
          </Link>
        )}

        <Link
          to={`/event/${eventId}/tickets`}
          className="sidebar-btn menu--link"
          title="Tickets"
          onClick={() => {
            setMobileNavBarOpen(false);
          }}
        >
          <div className="active-indicator" />
          <Ticket className="sidebar-icon" />
          <span className="sidebar-label">Tickets</span>
        </Link>

        {hasAgendaData && (
          <Link
            to={`/event/${eventId}/agenda`}
            className="sidebar-btn menu--link"
            title="Agenda"
            onClick={() => {
              setMobileNavBarOpen(false);
            }}
          >
            <div className="active-indicator" />
            <CheckSquare className="sidebar-icon" />
            <span className="sidebar-label">Agenda</span>
          </Link>
        )}

        <Link
          to={`/event/${eventId}/sponsor`}
          className="sidebar-btn menu--link"
          title="Sponsor"
          onClick={() => {
            setMobileNavBarOpen(false);
          }}
        >
          <div className="active-indicator" />
          <Heart className="sidebar-icon" />
          <span className="sidebar-label">Sponsor</span>
        </Link>

        <Link
          to={`/event/${eventId}/coupons`}
          className="sidebar-btn menu--link"
          title="Coupons"
          onClick={() => {
            setMobileNavBarOpen(false);
          }}
        >
          <div className="active-indicator" />
          <Percent className="sidebar-icon" />
          <span className="sidebar-label">Coupons</span>
        </Link>

        <Link
          to={`/event/${eventId}/lineup`}
          className="sidebar-btn menu--link"
          title="Lineup"
          onClick={() => {
            setMobileNavBarOpen(false);
          }}
        >
          <div className="active-indicator" />
          <Mic className="sidebar-icon" />
          <span className="sidebar-label">Line Up</span>
        </Link>
        
        <Link
          to={`/event/${eventId}/sell-ticket`}
          className="sidebar-btn menu--link"
          title="Sell Tickets"
          onClick={() => {
            setMobileNavBarOpen(false);
          }}
        >
          <div className="active-indicator" />
          <Coins className="sidebar-icon" />
          <span className="sidebar-label">Sell Tickets</span>
        </Link>

        <Link
          to={`/event/${eventId}/refund`}
          className="sidebar-btn menu--link"
          title="Refund"
          onClick={() => {
            setMobileNavBarOpen(false);
          }}
        >
          <div className="active-indicator" />
          <RotateCcw className="sidebar-icon" />
          <span className="sidebar-label">Refund</span>
        </Link>

        <Link
          to={`/event/${eventId}/run-report`}
          className="sidebar-btn menu--link"
          title="Reports"
          onClick={() => {
            setMobileNavBarOpen(false);
          }}
        >
          <div className="active-indicator" />
          <BarChart3 className="sidebar-icon" />
          <span className="sidebar-label">Reports</span>
        </Link>

        {isEventClosed && (
          <Link
            to={`/event/${eventId}/payout-receipt`}
            className="sidebar-btn menu--link"
            title="Payout Receipt"
            onClick={() => {
              setMobileNavBarOpen(false);
            }}
          >
            <div className="active-indicator" />
            <Receipt className="sidebar-icon" />
            <span className="sidebar-label">Payout Receipt</span>
          </Link>
        )}

        <Link
          to={`/event/${eventId}/contacts`}
          className="sidebar-btn menu--link"
          title="Contacts"
          onClick={() => {
            setMobileNavBarOpen(false);
          }}
        >
          <div className="active-indicator" />
          <UsersRound className="sidebar-icon" />
          <span className="sidebar-label">Contacts</span>
        </Link>

        <Link
          to={`/event/${eventId}/forum`}
          className="sidebar-btn menu--link"
          title="Event Enquiries"
          onClick={() => {
            setMobileNavBarOpen(false);
          }}
        >
          <div className="active-indicator" />
          <MessageSquare className="sidebar-icon" />
          <span className="sidebar-label">Forum</span>
        </Link>

        {hasSlotData && (
          <Link
            to={`/event/${eventId}/slot`}
            className="sidebar-btn menu--link"
            title="Slot Bookings"
            onClick={() => {
              setMobileNavBarOpen(false);
            }}
          >
            <div className="active-indicator" />
            <CalendarRange className="sidebar-icon" />
            <span className="sidebar-label">Slot Bookings</span>
          </Link>
        )}
      </div>

      {/* Bottom Actions - Logout, Theme & Identity */}
      <div className="sidebar-bottom-actions">
        {/* Adaptive Theme Toggle Option */}
        <button
          id="sidebar-theme-toggle"
          onClick={toggleNightMode}
          className="sidebar-btn"
          title="Switch App Theme"
        >
          <Palette className={`sidebar-icon ${isNightMode ? 'theme-pulse-icon' : ''}`} />
          <div className="profile-info">
            <span className="sub-meta-text">App Theme</span>
            <span className="theme-name">
              {isNightMode ? 'Eventox Original' : 'Classic Gray'}
            </span>
          </div>
        </button>

        {/* Logout Link */}
        <Link to={`/login`} className="sidebar-btn logout-btn menu--link" title="logout"
          onClick={() => {
            setMobileNavBarOpen(false);
            localStorage.clear();
          }}>
          <LogOut className="sidebar-icon logout-icon" />
          <span className="sidebar-label">Logout</span>
        </Link>

        {/* Organizer mini identity */}
        <Link
          to="/profile"
          id="sidebar-mini-profile"
          className="sidebar-mini-profile"
          onClick={() => {
            setMobileNavBarOpen(false);
          }}
        >
          <div className="profile-avatar">
            {profileName.split(' ').map(n => n[0]).join('') || 'O'}
          </div>
          <div className="profile-info">
            <span className="profile-name">{profileName}</span>
            <span className="profile-role">
              {userRole === "8"
                ? "Event co organizer"
                : userRole === "10"
                ? "Co organizer"
                : "Organizer"}
            </span>
          </div>
        </Link>
      </div>
    </nav>
  );
};

export default EventSideNavMenu;
